import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fetchContact, updateContact, fetchAssignableRoles } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Save, MapPin, X, ShieldCheck, Plus, MessageSquare, MailOpen } from "lucide-react";
import { fetchContactActivity } from "@/lib/api";

export function ContactActivity({ contactRecordId, callerUserRecordId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!contactRecordId || !callerUserRecordId) return;
    fetchContactActivity(contactRecordId, callerUserRecordId).then(setData).catch(() => setData(null));
  }, [contactRecordId, callerUserRecordId]);
  if (!data) return null;
  const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
  return (
    <div className="space-y-3" data-testid="contact-activity">
      <div className="rounded-xl border border-zinc-200 bg-white">
        <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-zinc-500 font-semibold flex items-center gap-1"><MailOpen size={12} /> Emails · {data.emails_sent} sent · {data.emails_opened} opened</div>
        <ul className="divide-y divide-zinc-100 max-h-40 overflow-y-auto">
          {data.emails.slice(0, 20).map((e) => (
            <li key={e.id} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs" data-testid={`contact-email-${e.id}`}>
              <div className="min-w-0"><div className="font-medium text-zinc-800 truncate">{e.template}</div><div className="text-[10px] text-zinc-400">{when(e.at)}</div></div>
              <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${e.opened ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{e.opened ? "Opened" : "Sent"}</span>
            </li>
          ))}
          {!data.emails.length && <li className="px-3 py-2 text-xs text-zinc-400">No emails yet.</li>}
        </ul>
      </div>
      <div className="rounded-xl border border-zinc-200 bg-white">
        <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-zinc-500 font-semibold flex items-center gap-1"><MessageSquare size={12} /> Messages · chat &amp; SMS</div>
        <ul className="divide-y divide-zinc-100 max-h-48 overflow-y-auto">
          {data.messages.slice(0, 30).map((m) => (
            <li key={m.id} className="px-3 py-1.5 text-xs" data-testid={`contact-msg-${m.id}`}>
              <div className="flex items-center gap-1.5 text-[10px] text-zinc-400">
                <span className={`uppercase font-bold px-1 rounded ${m.channel === "sms" ? "bg-emerald-100 text-emerald-700" : "bg-violet-100 text-violet-700"}`}>{m.channel}</span>
                <span>{m.sender === "user" ? "Customer" : "Team"}</span><span>· {when(m.created_at)}</span>
              </div>
              <div className="text-zinc-800 line-clamp-2">{m.message}</div>
            </li>
          ))}
          {!data.messages.length && <li className="px-3 py-2 text-xs text-zinc-400">No chat or text messages yet.</li>}
        </ul>
      </div>
    </div>
  );
}

/**
 * Contact edit sheet — opens straight into the editable form (name, email,
 * phone + full address). One tap from the pencil, no read-then-edit step.
 */
export default function ContactDetailSheet({
  open,
  onOpenChange,
  contactRowId,
  callerUserRecordId,
  onSaved,
  onAddOrder, // () => void — opens the Invoice / Add order sheet for this contact
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [contactRec, setContactRec] = useState("");
  const [roles, setRoles] = useState([]);
  const [currentRole, setCurrentRole] = useState("");
  const [roleId, setRoleId] = useState("");

  useEffect(() => {
    if (!open || !contactRowId) {
      setForm(null);
      return;
    }
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const [res, rr] = await Promise.all([fetchContact(contactRowId, callerUserRecordId), fetchAssignableRoles(callerUserRecordId).catch(() => null)]);
        if (!active) return;
        const c = res?.contact || {};
        setContactRec(c.contact_record_id || c.user_record_id || "");
        setRoles(rr?.roles || []);
        setCurrentRole((c.role || "").trim());
        setRoleId("");
        setForm({
          first_name: c.first_name || "",
          last_name: c.last_name || "",
          email: c.email || "",
          phone: c.phone || "",
          address: c.address || "",
          address2: c.address2 || "",
          city: c.city || "",
          state: c.state || "",
          postcode: c.postcode || "",
          country: c.country || "US",
          orevital_revenue: c.orevital_revenue || 0,
          mindful_minerals_revenue: c.mindful_minerals_revenue || 0,
          total_revenue: c.total_revenue || 0,
        });
      } catch (e) {
        if (active) toast.error(e?.response?.data?.detail || "Could not load");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, contactRowId, callerUserRecordId]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function save() {
    setSaving(true);
    try {
      await updateContact(contactRowId, callerUserRecordId, roleId ? { ...form, role_id: roleId } : form);
      if (roleId) setCurrentRole(roles.find((r) => r.id === roleId)?.role_name || currentRole);
      toast.success("Contact saved");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="sm:max-w-md sm:mx-auto rounded-t-2xl bg-white p-0 max-h-[92vh] overflow-y-auto [&>button]:hidden"
        data-testid="contact-detail-sheet"
      >
        <SheetHeader className="p-4 border-b border-zinc-100">
          <div className="flex items-center justify-between gap-3">
            <SheetTitle className="text-base font-semibold text-zinc-900">
              Edit contact
            </SheetTitle>
            <SheetDescription className="sr-only">
              Edit this contact's details, then save or close to return to the list.
            </SheetDescription>
            {onAddOrder && (
              <button
                type="button"
                onClick={() => onAddOrder()}
                className="h-9 px-3 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1 shrink-0"
                title="New order or invoice for this contact"
                data-testid="contact-detail-add-order"
              >
                <Plus size={12} /> Order
              </button>
            )}
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label="Close and return to CRM"
              data-testid="contact-detail-close"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </SheetHeader>

        <div className="p-4 space-y-3">
          {loading || !form ? (
            <div className="py-10 flex items-center justify-center text-zinc-500 text-sm">
              <Loader2 className="animate-spin mr-2" size={16} /> Loading…
            </div>
          ) : (
            <>
              <Grid2>
                <Field label="First name">
                  <Input value={form.first_name} onChange={set("first_name")} className="h-11" data-testid="contact-edit-first-name" />
                </Field>
                <Field label="Last name">
                  <Input value={form.last_name} onChange={set("last_name")} className="h-11" data-testid="contact-edit-last-name" />
                </Field>
              </Grid2>
              <Field label="Email">
                <Input type="email" value={form.email} onChange={set("email")} className="h-11" data-testid="contact-edit-email" />
              </Field>
              <Field label="Phone">
                <Input type="tel" value={form.phone} onChange={set("phone")} className="h-11" placeholder="+15555550123" data-testid="contact-edit-phone" />
              </Field>

              {roles.length > 0 && (
                <Field label="Role">
                  <Select value={roleId} onValueChange={setRoleId}>
                    <SelectTrigger className="h-11 rounded-xl" data-testid="contact-edit-role-select">
                      <SelectValue placeholder={currentRole ? `Current: ${currentRole}` : "Select role"} />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((r) => (
                        <SelectItem key={r.id} value={r.id} data-testid={`contact-role-option-${r.id}`}>
                          <span className="capitalize">{r.role_name}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1"><ShieldCheck size={11} /> You can only assign roles on your level or below — same scale as coupons.</p>
                </Field>
              )}

              <div className="flex items-center gap-2 pt-2 pb-1">
                <MapPin size={14} className="text-violet-600" />
                <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">
                  Address
                </span>
              </div>
              <Field label="Street">
                <Input value={form.address} onChange={set("address")} className="h-11" data-testid="contact-edit-address" />
              </Field>
              <Field label="Apt / suite">
                <Input value={form.address2} onChange={set("address2")} className="h-11" data-testid="contact-edit-address2" />
              </Field>
              <Grid2>
                <Field label="City">
                  <Input value={form.city} onChange={set("city")} className="h-11" data-testid="contact-edit-city" />
                </Field>
                <Field label="State">
                  <Input value={form.state} onChange={set("state")} className="h-11" data-testid="contact-edit-state" />
                </Field>
              </Grid2>
              <Grid2>
                <Field label="ZIP / postcode">
                  <Input value={form.postcode} onChange={set("postcode")} className="h-11" data-testid="contact-edit-postcode" />
                </Field>
                <Field label="Country">
                  <Input value={form.country} onChange={set("country")} className="h-11" data-testid="contact-edit-country" />
                </Field>
              </Grid2>

              <div
                className="mt-2 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2.5"
                data-testid="contact-brand-sales"
              >
                <div className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1.5">
                  Sales by brand
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-600">Mindful Minerals (MI)</span>
                  <span className="font-semibold text-zinc-900" data-testid="contact-mi-sales">
                    ${(form.mindful_minerals_revenue || 0).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-zinc-600">Orevital (OV)</span>
                  <span className="font-semibold text-zinc-900" data-testid="contact-ov-sales">
                    ${(form.orevital_revenue || 0).toFixed(2)}
                  </span>
                </div>
              </div>

              {contactRec && <ContactActivity contactRecordId={contactRec} callerUserRecordId={callerUserRecordId} />}

              <div className="flex gap-2 pt-3 pb-2">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="flex-1 h-11 rounded-xl"
                  data-testid="contact-edit-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={save}
                  disabled={saving}
                  className="flex-1 h-11 rounded-xl bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="contact-edit-save"
                >
                  {saving ? (
                    <>
                      <Loader2 size={14} className="animate-spin mr-1.5" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Save size={14} className="mr-1.5" /> Save
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] font-medium text-zinc-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Grid2({ children }) {
  return <div className="grid grid-cols-2 gap-3">{children}</div>;
}
